/**
 * Metric Aggregation Database
 *
 * Persistent, normalized timeseries cache for the metric aggregation RPC
 * response (metrics-part-2 sub-plan 005, task 001).
 *
 * The design separates *dataset identity* from *query bounds*: one dataset
 * spans every requested range for the same viewer / pipeline / subject /
 * granularity. Requests are recorded as half-open coverage intervals over the
 * dataset, and each response row becomes one ordinalized point record. This
 * lets overlapping 7-day / 30-day / 90-day / zoom windows share bucket storage
 * instead of duplicating whole query responses.
 *
 * Schema:
 * - datasets:          one row per dataset identity, carries coverage + descriptor
 * - points:            one row per server response point, ordinalized per bucket
 * - pipelineVersions:  monotonic generation per viewer+pipeline for rebuild
 *                      invalidation and stale-write rejection
 *
 * IndexedDB is the only persistent metric-data cache; nothing here logs
 * descriptors, subjects, series or metric values.
 */

import Dexie, { type Table } from 'dexie';
import type {
  AggregatedMetricsResponse,
  AggregationOutputPoint,
  MetricAggregationDescriptor,
} from '@bloomstack/apis/services/metrics/metricAggregationTypes';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Datasets not accessed within this window are evicted. */
export const METRIC_DATASET_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Maximum number of datasets retained in IndexedDB. */
export const METRIC_DATASET_MAX = 250;

/** Maximum number of point rows retained in IndexedDB. */
export const METRIC_POINT_MAX = 100_000;

/**
 * A read only refreshes `lastAccessedAt` when the stored value is at least this
 * old, so continuous reads do not create continuous IndexedDB writes.
 */
export const METRIC_ACCESS_TOUCH_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Sentinel stored in `subjectKey` for `subjectId === undefined`. It exists only
 * for indexing/diagnostics — dataset *identity* distinguishes undefined from
 * any real string without delimiter parsing (see `metricDatasetId`).
 */
const UNDEFINED_SUBJECT_KEY = '\u0000__undefined__';

// ============================================================================
// TYPES
// ============================================================================

/** Half-open coverage interval `[startMs, endMs)`. */
export type CoverageInterval = [number, number];

/** The identity + request bounds for a single cache query. */
export type MetricDatasetQuery = {
  viewerUserId: string;
  pipelineId: string;
  subjectId?: string;
  granularity: string;
};

/** One cached dataset — identity components plus metadata + coverage. */
export type MetricDatasetRecord = {
  id: string;
  viewerUserId: string;
  pipelineId: string;
  /** Stored sentinel for `undefined` (diagnostics only). */
  subjectKey: string;
  granularity: string;
  descriptor: MetricAggregationDescriptor;
  availableSubjects: string[];
  /** Sorted, non-overlapping, half-open coverage intervals. */
  coverage: CoverageInterval[];
  /** Pipeline generation this dataset was written under. */
  pipelineVersion: number;
  updatedAt: number;
  lastAccessedAt: number;
};

/** One server response row, ordinalized within its bucket. */
export type MetricPointRecord = {
  datasetId: string;
  /** Bucket timestamp as epoch ms (reconstructed as `Date` on read). */
  bucketMs: number;
  /** Position of this row among rows sharing the same bucket, in received order. */
  ordinal: number;
  subjectId: string;
  series: string;
  value: number;
};

/** Monotonic generation per viewer+pipeline. */
export type MetricPipelineVersionRecord = {
  viewerUserId: string;
  pipelineId: string;
  version: number;
};

/** Discriminated outcome of a cache write. */
export type MetricWriteResult = 'persisted' | 'oversize' | 'stale';

/** The compound primary key of a point record. */
export type MetricPointKey = [string, number, number];

// ============================================================================
// DATABASE
// ============================================================================

export class MetricAggregationDatabase extends Dexie {
  datasets!: Table<MetricDatasetRecord, string>;
  points!: Table<MetricPointRecord, MetricPointKey>;
  pipelineVersions!: Table<MetricPipelineVersionRecord, [string, string]>;

  constructor(name = 'BloomstackMetricAggregations') {
    super(name);
    this.version(1).stores({
      datasets: 'id, [viewerUserId+pipelineId], lastAccessedAt',
      points: '[datasetId+bucketMs+ordinal], datasetId, [datasetId+bucketMs]',
      pipelineVersions: '[viewerUserId+pipelineId]',
    });
  }
}

/** Production singleton. Tests construct their own isolated database. */
export const metricAggregationDb = new MetricAggregationDatabase();

/** Run bounded eviction against the production cache (startup + after writes). */
export async function evictMetricAggregationCache(): Promise<number> {
  return evictMetricCache(metricAggregationDb);
}

/**
 * Explicitly purge one viewer's persisted metric data. Exported for the profile
 * logout path, which must await the clear before the redirecting Keycloak logout
 * (a React cleanup/effect would run too late).
 */
export async function clearMetricAggregationCache(viewerUserId: string): Promise<void> {
  if (!viewerUserId) return;
  await purgeViewers(metricAggregationDb, [viewerUserId]);
}

// ============================================================================
// PURE ALGORITHMS
// ============================================================================

/**
 * Collision-free dataset ID. `from`/`to` never appear — ranges are coverage,
 * not identity. JSON encoding means delimiter-like characters in any component
 * cannot collide.
 */
export function metricDatasetId(
  viewerUserId: string,
  pipelineId: string,
  subjectId: string | undefined,
  granularity: string,
): string {
  return JSON.stringify(['metrics-v1', viewerUserId, pipelineId, subjectId ?? null, granularity]);
}

/** Stored sentinel for the subject component of a dataset. */
export function metricSubjectKey(subjectId: string | undefined): string {
  return subjectId === undefined ? UNDEFINED_SUBJECT_KEY : subjectId;
}

/**
 * Sort, merge overlapping/adjacent intervals, and drop empty ones. Input and
 * output are half-open `[start, end)` intervals; adjacency (`start === lastEnd`)
 * merges so a contiguously-covered span becomes a single interval.
 */
export function normalizeCoverage(intervals: ReadonlyArray<CoverageInterval>): CoverageInterval[] {
  const sortable = intervals
    .filter(([start, end]) => end > start)
    .map(([start, end]): CoverageInterval => [start, end])
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  const merged: CoverageInterval[] = [];
  for (const [start, end] of sortable) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) {
      if (end > last[1]) last[1] = end;
    } else {
      merged.push([start, end]);
    }
  }
  return merged;
}

/**
 * True only when the union of `coverage` contains every millisecond of the
 * half-open `[from, to)`. Because coverage is normalized (merged and sorted),
 * a fully-covered range is always contained in a single interval.
 */
export function coverageContains(
  coverage: ReadonlyArray<CoverageInterval>,
  from: number,
  to: number,
): boolean {
  if (to <= from) return true;
  for (const [start, end] of coverage) {
    if (start <= from && end >= to) return true;
    if (start > from) break;
  }
  return false;
}

// ============================================================================
// HELPERS
// ============================================================================

async function readPipelineVersion(
  db: MetricAggregationDatabase,
  viewerUserId: string,
  pipelineId: string,
): Promise<number> {
  const record = await db.pipelineVersions.get([viewerUserId, pipelineId]);
  return record?.version ?? 0;
}

/** Delete whole datasets and their point rows. Caller supplies a transaction. */
async function deleteDatasetsTx(
  db: MetricAggregationDatabase,
  ids: readonly string[],
): Promise<void> {
  if (ids.length === 0) return;
  await db.points.where('datasetId').anyOf(ids as string[]).delete();
  await db.datasets.bulkDelete(ids as string[]);
}

// ============================================================================
// READ
// ============================================================================

/**
 * Read a complete cached range. Returns `null` on any miss: missing dataset,
 * generation mismatch, or uncovered range. A partial range is deliberately a
 * miss — rendering it as complete would make absent buckets look like zeroes.
 *
 * Confirmed-empty ranges (coverage recorded with no point rows) return an empty
 * `points` array rather than `null`.
 */
export async function readMetricRange(
  db: MetricAggregationDatabase,
  query: MetricDatasetQuery,
  from: Date,
  to: Date,
): Promise<AggregatedMetricsResponse | null> {
  const id = metricDatasetId(query.viewerUserId, query.pipelineId, query.subjectId, query.granularity);
  const fromMs = from.getTime();
  const toMs = to.getTime();

  const dataset = await db.datasets.get(id);
  if (!dataset) return null;

  const version = await readPipelineVersion(db, query.viewerUserId, query.pipelineId);
  if (dataset.pipelineVersion !== version) return null;
  if (!coverageContains(dataset.coverage, fromMs, toMs)) return null;

  const rows = await db.points
    .where('[datasetId+bucketMs]')
    .between([id, fromMs], [id, toMs], true, false)
    .toArray();

  rows.sort((a, b) => a.bucketMs - b.bucketMs || a.ordinal - b.ordinal);

  const points: AggregationOutputPoint[] = rows.map((row) => ({
    subjectId: row.subjectId,
    series: row.series,
    bucket: new Date(row.bucketMs),
    value: row.value,
  }));

  // Throttled access touch — never creates writes on every read.
  if (Date.now() - dataset.lastAccessedAt >= METRIC_ACCESS_TOUCH_MS) {
    await db.datasets.update(id, { lastAccessedAt: Date.now() });
  }

  return {
    descriptor: dataset.descriptor,
    points,
    availableSubjects: dataset.availableSubjects,
  };
}

// ============================================================================
// WRITE
// ============================================================================

/**
 * Atomically replace the fetched `[from, to)` slice, preserving duplicate rows
 * returned by the server (assigned distinct same-bucket ordinals) and removing
 * rows that disappeared server-side.
 *
 * - Returns `'oversize'` (no write) when the response exceeds {@link METRIC_POINT_MAX}.
 * - Returns `'stale'` (no write) when the pipeline generation changed since
 *   `capturedVersion` was read — a rebuild invalidated this data mid-flight.
 *
 * Coverage is merged even for zero-point responses, so "confirmed empty" is
 * distinguishable from "not fetched".
 */
export async function writeMetricRange(
  db: MetricAggregationDatabase,
  query: MetricDatasetQuery,
  from: Date,
  to: Date,
  response: AggregatedMetricsResponse,
  capturedVersion: number,
  options: { evict?: boolean } = {},
): Promise<MetricWriteResult> {
  if (response.points.length > METRIC_POINT_MAX) {
    return 'oversize';
  }

  const id = metricDatasetId(query.viewerUserId, query.pipelineId, query.subjectId, query.granularity);
  const fromMs = from.getTime();
  const toMs = to.getTime();

  const result = await db.transaction('rw', db.datasets, db.points, db.pipelineVersions, async (): Promise<MetricWriteResult> => {
    const version = await readPipelineVersion(db, query.viewerUserId, query.pipelineId);
    if (version !== capturedVersion) return 'stale';

    await db.points
      .where('[datasetId+bucketMs]')
      .between([id, fromMs], [id, toMs], true, false)
      .delete();

    const ordinals = new Map<number, number>();
    const records: MetricPointRecord[] = [];
    for (const point of response.points) {
      const bucketMs = point.bucket.getTime();
      const ordinal = ordinals.get(bucketMs) ?? 0;
      ordinals.set(bucketMs, ordinal + 1);
      records.push({
        datasetId: id,
        bucketMs,
        ordinal,
        subjectId: point.subjectId,
        series: point.series,
        value: point.value,
      });
    }
    if (records.length > 0) {
      await db.points.bulkPut(records);
    }

    const existing = await db.datasets.get(id);
    const previousCoverage = existing && existing.pipelineVersion === version ? existing.coverage : [];
    const coverage = normalizeCoverage([...previousCoverage, [fromMs, toMs]]);

    const now = Date.now();
    const dataset: MetricDatasetRecord = {
      id,
      viewerUserId: query.viewerUserId,
      pipelineId: query.pipelineId,
      subjectKey: metricSubjectKey(query.subjectId),
      granularity: query.granularity,
      descriptor: response.descriptor,
      availableSubjects: response.availableSubjects,
      coverage,
      pipelineVersion: version,
      updatedAt: now,
      lastAccessedAt: now,
    };
    await db.datasets.put(dataset);

    return 'persisted';
  });

  if (result === 'persisted' && options.evict !== false) {
    await evictMetricCache(db);
  }

  return result;
}

/**
 * Capture the current pipeline generation. Callers read this immediately before
 * starting the RPC so a rebuild invalidation mid-flight can reject the write.
 */
export async function capturePipelineVersion(
  db: MetricAggregationDatabase,
  viewerUserId: string,
  pipelineId: string,
): Promise<number> {
  return readPipelineVersion(db, viewerUserId, pipelineId);
}

// ============================================================================
// INVALIDATION
// ============================================================================

/**
 * Invalidate every dataset for a viewer+pipeline and bump its generation. The
 * generation bump rejects any older in-flight response from restoring
 * invalidated data. Runs as one transaction across all three tables.
 */
export async function invalidatePipeline(
  db: MetricAggregationDatabase,
  viewerUserId: string,
  pipelineId: string,
): Promise<void> {
  await db.transaction('rw', db.datasets, db.points, db.pipelineVersions, async () => {
    const existing = await db.pipelineVersions.get([viewerUserId, pipelineId]);
    const next = (existing?.version ?? 0) + 1;
    await db.pipelineVersions.put({ viewerUserId, pipelineId, version: next });

    const datasets = await db.datasets
      .where('[viewerUserId+pipelineId]')
      .equals([viewerUserId, pipelineId])
      .toArray();
    await deleteDatasetsTx(db, datasets.map((dataset) => dataset.id));
  });
}

/**
 * Purge every dataset (and its points) belonging to a set of viewers, plus their
 * pipeline generations. Used on explicit logout, auth loss, and account switch.
 */
export async function purgeViewers(
  db: MetricAggregationDatabase,
  viewerUserIds: readonly string[],
): Promise<void> {
  if (viewerUserIds.length === 0) return;
  const targets = new Set(viewerUserIds);

  await db.transaction('rw', db.datasets, db.points, db.pipelineVersions, async () => {
    const datasets = await db.datasets.filter((dataset) => targets.has(dataset.viewerUserId)).toArray();
    await deleteDatasetsTx(db, datasets.map((dataset) => dataset.id));
    await db.pipelineVersions.filter((record) => targets.has(record.viewerUserId)).delete();
  });
}

/**
 * Purge every viewer *except* the given one. Used when a viewer becomes
 * established so no other account's admin metric data lingers locally.
 */
export async function purgeOtherViewers(
  db: MetricAggregationDatabase,
  keepViewerUserId: string,
): Promise<void> {
  await db.transaction('rw', db.datasets, db.points, db.pipelineVersions, async () => {
    const all = await db.datasets.toArray();
    const ids = all.filter((dataset) => dataset.viewerUserId !== keepViewerUserId).map((dataset) => dataset.id);
    await deleteDatasetsTx(db, ids);
    await db.pipelineVersions
      .filter((record) => record.viewerUserId !== keepViewerUserId)
      .delete();
  });
}

// ============================================================================
// EVICTION
// ============================================================================

/**
 * Bound storage by deleting whole datasets. Runs after startup and after every
 * committed write. Order:
 *   1. datasets not accessed within `ttlMs` (default 30 days)
 *   2. least-recently-accessed datasets until both limits hold
 * Metadata and point rows are removed together, so no orphan points remain.
 *
 * Limits default to the production constants; tests override them to exercise
 * the bounding logic without materializing 100k rows.
 */
export async function evictMetricCache(
  db: MetricAggregationDatabase,
  options: { ttlMs?: number; maxDatasets?: number; maxPoints?: number } = {},
): Promise<number> {
  const ttlMs = options.ttlMs ?? METRIC_DATASET_TTL_MS;
  const maxDatasets = options.maxDatasets ?? METRIC_DATASET_MAX;
  const maxPoints = options.maxPoints ?? METRIC_POINT_MAX;

  return db.transaction('rw', db.datasets, db.points, async () => {
    let deleted = 0;
    const now = Date.now();

    const stale = await db.datasets.where('lastAccessedAt').below(now - ttlMs).toArray();
    if (stale.length > 0) {
      await deleteDatasetsTx(db, stale.map((dataset) => dataset.id));
      deleted += stale.length;
    }

    const remaining = await db.datasets.orderBy('lastAccessedAt').toArray();
    let pointRows = await db.points.count();
    let datasetsLeft = remaining.length;

    for (const dataset of remaining) {
      if (datasetsLeft <= maxDatasets && pointRows <= maxPoints) break;
      const count = await db.points.where('datasetId').equals(dataset.id).count();
      await deleteDatasetsTx(db, [dataset.id]);
      datasetsLeft -= 1;
      pointRows -= count;
      deleted += 1;
    }

    return deleted;
  });
}

